import React from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { closeModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import Button from 'components/Button';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import MessageBox from 'components/MessageBox';
import Modal from 'components/Modal';
// utils
import classnames from 'classnames/bind';
// styles
import styles from './CombinedTreatmentProtocolModal.module.scss';

const cn = classnames.bind(styles);

const confirmLabels = {
  disable: 'general.button.disable',
  enable: 'general.button.enable',
  delete: 'general.button.delete',
  edit: 'general.button.continue',
};

// Tells which copies of the group a Disable / Enable / Delete / Edit of a combined protocol entry
// touches before it runs; copies that differ are picked one by one for editing
const CombinedTreatmentProtocolModal = ({
  action,
  treatmentProtocol: { protocols, fully_identical },
  groupName,
  onConfirm,
  onEditCopy,
  closeModal,
}) => {
  const isEditedSeparately = action === 'edit' && !fully_identical;
  const title = <FormattedMessage id={`component.modal.combinedTreatmentProtocol.title.${action}`} />;

  const handleEditCopy = (protocol) => {
    closeModal();
    onEditCopy(protocol);
  };

  const body = (
    <>
      <FormattedMessage
        id="component.modal.combinedTreatmentProtocol.copies"
        tagName="p"
        values={{ count: protocols.length, groupName, b: (msg) => <b>{msg}</b> }}
      />
      <ul className={cn('copies')}>
        {protocols.map((protocol) => (
          <li key={protocol.id} className={cn('copy')}>
            <div>
              <div className="semibold">{protocol.name}</div>
              {protocol.source_farm && (
                <FormattedMessage
                  id="component.modal.combinedTreatmentProtocol.fromSowUnit"
                  values={{ sowUnit: protocol.source_farm.name }}
                />
              )}
            </div>
            {isEditedSeparately && (
              <Button small default onClick={() => handleEditCopy(protocol)}>
                <FormattedMessage id="general.button.edit" />
              </Button>
            )}
          </li>
        ))}
      </ul>
      <FormattedMessage
        id={`component.modal.combinedTreatmentProtocol.${isEditedSeparately ? 'editSeparately' : action}`}
        tagName="p"
      />
      <MessageBox type="warning" isBorderHidden>
        <FormattedMessage id="component.modal.combinedTreatmentProtocol.scope" />
      </MessageBox>
    </>
  );

  if (isEditedSeparately) {
    return (
      <Modal title={title}>
        <section className="modal-body">{body}</section>
        <section className="modal-footer text-right">
          <Button onClick={closeModal}>
            <FormattedMessage id="general.button.cancel" />
          </Button>
        </section>
      </Modal>
    );
  }

  return (
    <ConfirmationModal
      title={title}
      actionBtnLabel={<FormattedMessage id={confirmLabels[action]} />}
      actionBtnProps={{ primary: true }}
      handleConfirm={onConfirm}
    >
      {body}
    </ConfirmationModal>
  );
};

CombinedTreatmentProtocolModal.propTypes = {
  action: T.oneOf(['disable', 'enable', 'delete', 'edit']).isRequired,
  treatmentProtocol: T.shape({
    protocols: T.arrayOf(T.shape({
      id: T.number.isRequired,
      name: T.string,
      source_farm: T.shape({ name: T.string }),
    })).isRequired,
    fully_identical: T.bool,
  }).isRequired,
  groupName: T.string,
  onConfirm: T.func,
  onEditCopy: T.func,
  closeModal: T.func.isRequired,
};

CombinedTreatmentProtocolModal.defaultProps = {
  groupName: '',
  onConfirm: () => {},
  onEditCopy: () => {},
};

export default connect(null, { closeModal })(CombinedTreatmentProtocolModal);
